import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { subscribeEmail } from '../lib/api';
import { toast } from 'sonner';
import { Mail, CheckCircle } from 'lucide-react';

export default function SignupPage() {
  const [email, setEmail] = useState('');
  const [frequency, setFrequency] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [message, setMessage] = useState('');

  const isValidEmail = email && email.includes('@') && email.includes('.');
  const canSubmit = isValidEmail && frequency;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isValidEmail) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    if (!frequency) {
      toast.error('Please select a frequency preference');
      return;
    }

    setLoading(true);
    try {
      const result = await subscribeEmail(email, frequency);
      
      setSuccess(true);
      setMessage(result.message);
      
      if (result.status === 'already_subscribed') {
        toast.info(result.message);
      } else {
        toast.success('Check your email to confirm your subscription');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Something went wrong. Please try again.';
      toast.error(errorMsg);
      setMessage(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Left side - Image */}
      <div 
        className="hidden lg:block lg:w-[40%] bg-cover bg-center"
        style={{ 
          backgroundImage: 'url(https://images.unsplash.com/photo-1772140921186-90b409199621?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGFyY2hpdGVjdHVyZSUyMGNvbHVtbnMlMjBsaWdodGluZ3xlbnwwfHx8fDE3NzQ5ODc1ODV8MA&ixlib=rb-4.1.0&q=85)',
        }}
      />
      
      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 lg:p-24 bg-[#F9F9F6] noise-bg">
        <div className="w-full max-w-lg relative z-10">
          {!success ? (
            <>
              <h1 
                className="text-4xl lg:text-5xl xl:text-6xl mb-6 text-[#1A2E4A] tracking-tight"
                data-testid="signup-heading"
              >
                Digital Law Digest
              </h1>
              
              <p className="text-lg text-[#525C6A] mb-12 leading-relaxed">
                Be the first to know about every development in UK and EU data protection, AI, and digital law.
              </p>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-[#0A0F1A] mb-2">
                    Email Address
                  </label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full h-12 px-4 border-[#E2E4E0] bg-white focus:border-[#1A2E4A] focus:ring-1 focus:ring-[#1A2E4A]"
                    data-testid="signup-email-input"
                    disabled={loading}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-[#0A0F1A] mb-3">
                    How often would you like to receive the digest?
                  </label>
                  <RadioGroup 
                    value={frequency} 
                    onValueChange={setFrequency}
                    className="space-y-3"
                    data-testid="frequency-selection"
                  >
                    <div className="flex items-center space-x-3 p-3 border border-[#E2E4E0] bg-white hover:border-[#1A2E4A] transition-colors cursor-pointer">
                      <RadioGroupItem 
                        value="daily" 
                        id="daily" 
                        className="border-[#E2E4E0] data-[state=checked]:border-[#1A2E4A] data-[state=checked]:bg-[#1A2E4A]"
                        data-testid="frequency-daily"
                      />
                      <Label htmlFor="daily" className="cursor-pointer flex-1">
                        <span className="font-medium text-[#0A0F1A]">Daily (Monday–Friday)</span>
                      </Label>
                    </div>
                    
                    <div className="flex items-center space-x-3 p-3 border border-[#E2E4E0] bg-white hover:border-[#1A2E4A] transition-colors cursor-pointer">
                      <RadioGroupItem 
                        value="biweekly" 
                        id="biweekly"
                        className="border-[#E2E4E0] data-[state=checked]:border-[#1A2E4A] data-[state=checked]:bg-[#1A2E4A]"
                        data-testid="frequency-biweekly"
                      />
                      <Label htmlFor="biweekly" className="cursor-pointer flex-1">
                        <span className="font-medium text-[#0A0F1A]">Biweekly (Monday & Thursday)</span>
                      </Label>
                    </div>
                    
                    <div className="flex items-center space-x-3 p-3 border border-[#E2E4E0] bg-white hover:border-[#1A2E4A] transition-colors cursor-pointer">
                      <RadioGroupItem 
                        value="weekly" 
                        id="weekly"
                        className="border-[#E2E4E0] data-[state=checked]:border-[#1A2E4A] data-[state=checked]:bg-[#1A2E4A]"
                        data-testid="frequency-weekly"
                      />
                      <Label htmlFor="weekly" className="cursor-pointer flex-1">
                        <span className="font-medium text-[#0A0F1A]">Weekly (Monday only)</span>
                      </Label>
                    </div>
                  </RadioGroup>
                  {!frequency && (
                    <p className="text-sm text-[#C67D3B] mt-2" data-testid="frequency-validation-message">
                      Please select how often you'd like to receive the digest.
                    </p>
                  )}
                </div>
                
                <p className="text-sm text-[#525C6A]">
                  For information on how we may use your personal data, please see our{' '}
                  <Link to="/privacy" className="text-[#1A2E4A] underline">privacy notice</Link>.
                  {' '}For the terms on which this service is provided, please see our{' '}
                  <Link to="/terms" className="text-[#1A2E4A] underline">terms of use</Link>.
                </p>
                
                <Button
                  type="submit"
                  disabled={!canSubmit || loading}
                  className="w-full h-14 bg-[#1A2E4A] hover:bg-[#2B4468] text-white font-semibold text-base disabled:opacity-50 disabled:cursor-not-allowed rounded-none transition-colors duration-300"
                  data-testid="signup-submit-button"
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Subscribing...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <Mail className="w-5 h-5" />
                      Subscribe
                    </span>
                  )}
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center fade-in" data-testid="signup-success-message">
              <div className="w-16 h-16 bg-[#2E5A44] rounded-none flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-3xl text-[#1A2E4A] mb-4">Check Your Email</h2>
              <p className="text-lg text-[#525C6A] leading-relaxed">
                {message}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
